<template>
    <div class="camp-order-panel">
        <b-card class="mb-3">
            <!-- ① 訂單標頭 -->
            <div class="d-flex justify-content-between align-items-start camp-order-heading">
                <div class="camp-order-identity">
                    <div class="d-flex flex-wrap align-items-center">
                        <span class="h5 mb-0 font-weight-bold camp-order-number">訂單編號 {{ order.OrderNo }}</span>
                        <b-button variant="link" class="camp-order-copy ml-2" aria-label="複製訂單編號"
                            v-b-tooltip.hover title="複製訂單編號" @click="copyOrderNo">
                            <b-icon icon="clipboard"></b-icon>
                        </b-button>
                    </div>
                    <small class="text-muted d-block mt-1">下單時間 {{ order.Create | datefmt('YYYY-MM-DD HH:mm') }}</small>
                </div>
                <b-badge :variant="orderStateVariant" class="camp-order-state">{{ order.OrderState }}</b-badge>
            </div>
            <div v-if="order.Memo" class="mt-2 small">
                <span class="text-muted">訂單備註：</span>{{ order.Memo }}
            </div>

            <div v-if="attention" class="camp-attention-card mt-3" :class="'is-' + attention.type">
                <div class="camp-attention-icon" aria-hidden="true">
                    <b-icon :icon="attention.icon"></b-icon>
                </div>
                <div class="camp-attention-content">
                    <div class="camp-attention-eyebrow">{{ attention.eyebrow }}</div>
                    <div class="camp-attention-title">{{ attention.title }}</div>
                    <div class="camp-attention-description">{{ attention.description }}</div>
                </div>
                <b-button v-if="attention.actionLabel" :variant="attention.type === 'needs-action' ? 'primary' : 'outline-primary'"
                    class="camp-attention-action" @click="handleAttentionAction">
                    {{ attention.actionLabel }}
                    <b-icon icon="chevron-right" class="ml-1"></b-icon>
                </b-button>
            </div>

            <hr class="camp-order-hr" />

            <!-- ② 報名項目 -->
            <div class="camp-form-section-title">報名項目</div>
            <div class="d-none d-md-block">
                <b-table-simple small responsive class="mb-0 camp-registration-table camp-member-table">
                    <b-thead>
                        <b-tr>
                            <b-th style="width: 15%;">學員</b-th>
                            <b-th style="width: 45%;">課程 / 營隊 / 梯次</b-th>
                            <b-th style="width: 16%;">應付金額</b-th>
                            <b-th style="width: 14%;">目前狀態</b-th>
                            <b-th style="width: 10%;">操作</b-th>
                        </b-tr>
                    </b-thead>
                    <b-tbody>
                        <b-tr v-for="d in orderedDetails" :key="d.Id" :class="{ 'camp-detail-transferred': d.TransferredToTitle }">
                            <b-td class="align-middle">{{ d.RelationName }}</b-td>
                            <b-td class="align-middle">
                                <div v-if="d.CampSeriesTitle" class="small text-muted">{{ d.CampSeriesTitle }}</div>
                                <div>{{ d.CampProgramTitle }} / {{ d.CampProductTitle }}</div>
                                <div v-if="d.CampProductStart" class="small text-muted">
                                    {{ d.CampProductStart | datefmt('YYYY/MM/DD') }} ~ {{ d.CampProductEnd | datefmt('YYYY/MM/DD') }}
                                </div>
                                <div v-if="d.TransferredFromTitle" class="small text-muted mt-1">
                                    <b-icon icon="arrow-return-right"></b-icon> 轉班自 {{ d.TransferredFromTitle }}
                                </div>
                                <div v-if="d.TransferredToTitle" class="small text-info mt-1">
                                    <b-icon icon="arrow-right-circle"></b-icon> 已轉班至 {{ d.TransferredToTitle }}
                                </div>
                                <div v-if="d.IsCampAddon" class="mt-1">
                                    <b-badge variant="danger" pill>加購項目</b-badge>
                                    <span v-if="d.SourceCampProgramTitle" class="small text-muted d-block mt-1">
                                        來源：{{ d.SourceCampProgramTitle }}<span v-if="d.SourceCampProductTitle"> / {{ d.SourceCampProductTitle }}</span>
                                    </span>
                                </div>
                            </b-td>
                            <b-td class="align-middle">
                                <template v-if="!d.TransferredToTitle">
                                    <div class="font-weight-bold">{{ d.Price | money(0, 2) }}</div>
                                    <div class="small text-muted">
                                        原價 {{ d.OriginalPrice | money(0, 2) }}<span v-if="d.Discount">・折抵 -{{ d.Discount | money(0, 2) }}</span>
                                    </div>
                                </template>
                                <span v-else class="text-muted">—</span>
                            </b-td>
                            <b-td class="align-middle">
                                <b-badge :variant="detailStatusVariant(d.Status)">{{ d.TransferredToTitle ? '已轉班' : d.Status }}</b-badge>
                            </b-td>
                            <b-td class="align-middle">
                                <b-button v-if="d.CanCancel" size="sm" variant="outline-danger"
                                    @click="$emit('cancel-detail', order, d)">取消</b-button>
                                <b-button v-else-if="d.CanApplyRefundDetail" size="sm" variant="outline-secondary" class="text-nowrap"
                                    @click="$emit('apply-refund', { order, detail: d })">申請退費</b-button>
                                <span v-else-if="d.IsInActiveRefundScope" class="small text-muted">
                                    {{ d.ApplyRefundDisabledReason || '退費處理中' }}
                                </span>
                            </b-td>
                        </b-tr>
                    </b-tbody>
                </b-table-simple>
            </div>
            <div class="d-md-none">
                <div v-for="d in mobileVisibleDetails" :key="d.Id" class="camp-reg-card"
                    :class="{ 'camp-detail-transferred': d.TransferredToTitle }">
                    <div class="d-flex justify-content-between align-items-start">
                        <span class="font-weight-bold">{{ d.RelationName }}</span>
                        <b-badge :variant="detailStatusVariant(d.Status)">{{ d.TransferredToTitle ? '已轉班' : d.Status }}</b-badge>
                    </div>
                    <div v-if="d.CampSeriesTitle" class="small text-muted mt-1">{{ d.CampSeriesTitle }}</div>
                    <div class="small">{{ d.CampProgramTitle }} / {{ d.CampProductTitle }}</div>
                    <div v-if="d.CampProductStart" class="small text-muted">
                        {{ d.CampProductStart | datefmt('YYYY/MM/DD') }} ~ {{ d.CampProductEnd | datefmt('YYYY/MM/DD') }}
                    </div>
                    <div v-if="d.TransferredFromTitle" class="small text-muted mt-1">
                        <b-icon icon="arrow-return-right"></b-icon> 轉班自 {{ d.TransferredFromTitle }}
                    </div>
                    <div v-if="d.TransferredToTitle" class="small text-info mt-1">
                        <b-icon icon="arrow-right-circle"></b-icon> 已轉班至 {{ d.TransferredToTitle }}
                    </div>
                    <div v-if="d.IsCampAddon" class="mt-1">
                        <b-badge variant="danger" pill>加購項目</b-badge>
                        <span v-if="d.SourceCampProgramTitle" class="small text-muted d-block mt-1">
                            來源：{{ d.SourceCampProgramTitle }}<span v-if="d.SourceCampProductTitle"> / {{ d.SourceCampProductTitle }}</span>
                        </span>
                    </div>
                    <div v-if="!d.TransferredToTitle" class="camp-reg-card-amount mt-2">
                        <span class="small text-muted">原價 {{ d.OriginalPrice | money(0, 2) }}<span v-if="d.Discount">・折抵 -{{ d.Discount | money(0, 2) }}</span></span>
                        <span class="font-weight-bold">應付 {{ d.Price | money(0, 2) }}</span>
                    </div>
                    <div v-if="d.CanCancel || d.CanApplyRefundDetail || d.IsInActiveRefundScope" class="text-right mt-2">
                        <b-button v-if="d.CanCancel" size="sm" variant="outline-danger"
                            @click="$emit('cancel-detail', order, d)">取消</b-button>
                        <b-button v-else-if="d.CanApplyRefundDetail" size="sm" variant="outline-secondary" class="text-nowrap"
                            @click="$emit('apply-refund', { order, detail: d })">申請退費</b-button>
                        <span v-else class="small text-muted">{{ d.ApplyRefundDisabledReason || '退費處理中' }}</span>
                    </div>
                </div>
                <b-button v-if="transferredDetails.length" variant="link" class="camp-mobile-more-details"
                    @click="transferredDetailsOpen = !transferredDetailsOpen">
                    {{ transferredDetailsOpen ? '收合轉班前項目' : '查看更多（' + transferredDetails.length + '）' }}
                    <b-icon :icon="transferredDetailsOpen ? 'chevron-up' : 'chevron-down'" class="ml-1"></b-icon>
                </b-button>
            </div>

            <hr class="camp-order-hr" />

            <!-- ③ 金額小計 -->
            <button type="button" class="camp-mobile-collapse-toggle d-md-none" @click="amountOpen = !amountOpen"
                :aria-expanded="amountOpen ? 'true' : 'false'">
                <span>訂單金額明細</span>
                <span class="font-weight-bold">
                    {{ order.TotalPrice | money(0, 2) }}
                    <b-icon :icon="amountOpen ? 'chevron-up' : 'chevron-down'" class="ml-1"></b-icon>
                </span>
            </button>
            <dl class="camp-amount-summary mb-0 d-none d-md-block">
                <div>
                    <dt>訂單金額</dt>
                    <dd>{{ order.OrderPrice | money(0, 2) }}</dd>
                </div>
                <div>
                    <dt>折抵金額</dt>
                    <dd>{{ order.PromotePrice ? '-' + formatMoney(order.PromotePrice) : formatMoney(0) }}</dd>
                </div>
                <div class="camp-amount-total">
                    <dt>應付金額</dt>
                    <dd>{{ order.TotalPrice | money(0, 2) }}</dd>
                </div>
            </dl>
            <b-collapse v-model="amountOpen" class="d-md-none">
                <dl class="camp-amount-summary mb-0 mt-2">
                    <div>
                        <dt>訂單金額</dt>
                        <dd>{{ order.OrderPrice | money(0, 2) }}</dd>
                    </div>
                    <div>
                        <dt>折抵金額</dt>
                        <dd>{{ order.PromotePrice ? '-' + formatMoney(order.PromotePrice) : formatMoney(0) }}</dd>
                    </div>
                    <div class="camp-amount-total">
                        <dt>應付金額</dt>
                        <dd>{{ order.TotalPrice | money(0, 2) }}</dd>
                    </div>
                </dl>
            </b-collapse>

            <!-- ④ 付款 -->
            <template v-if="order.Payment">
                <hr class="camp-order-hr" />
                <div id="order-payment" class="camp-scroll-target"></div>

                <!-- 已付款：付款完成（現金／轉帳各自呈現） -->
                <template v-if="isPaid">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                        <div class="camp-form-section-title mb-0">付款資訊</div>
                        <b-badge variant="success"><b-icon icon="check-circle-fill" class="mr-1"></b-icon>已完成付款</b-badge>
                    </div>
                    <div class="camp-finance-card is-completed">
                    <dl class="camp-bank-list mb-0">
                        <div>
                            <dt>繳費方式</dt>
                            <dd>{{ order.Payment.PaymentMethod || '-' }}</dd>
                        </div>
                        <div>
                            <dt>繳費金額</dt>
                            <dd>{{ order.Payment.Amount | money(0, 2) }}</dd>
                        </div>
                        <template v-if="isCounterPayment">
                            <div v-if="order.Payment.ConfirmedAt">
                                <dt>核帳完成日</dt>
                                <dd>{{ order.Payment.ConfirmedAt | datefmt('YYYY-MM-DD') }}</dd>
                            </div>
                        </template>
                        <template v-else>
                            <div v-if="order.Payment.PaidDate">
                                <dt>付款日期</dt>
                                <dd>{{ order.Payment.PaidDate | datefmt('YYYY-MM-DD') }}</dd>
                            </div>
                            <div v-if="order.Payment.AccountLast5">
                                <dt>帳號後五碼</dt>
                                <dd>{{ order.Payment.AccountLast5 }}</dd>
                            </div>
                            <div v-if="order.Payment.PayerName">
                                <dt>付款人戶名</dt>
                                <dd>{{ order.Payment.PayerName }}</dd>
                            </div>
                            <div v-if="order.Payment.Memo">
                                <dt>備註</dt>
                                <dd>{{ order.Payment.Memo }}</dd>
                            </div>
                            <div>
                                <dt>付款狀態</dt>
                                <dd>{{ paidBannerText }}</dd>
                            </div>
                        </template>
                    </dl>
                    <div v-if="!isCounterPayment && order.Payment.HasProofImage" class="mt-3">
                        <b-button size="sm" variant="link" class="p-0"
                            @click="$emit('view-proof', order.Payment.Id)">查看付款截圖</b-button>
                    </div>
                    </div>
                </template>

                <!-- 現金結帳（未付款）：不顯示匯款回報欄位 -->
                <template v-else-if="isCounterPayment">
                    <div class="camp-form-section-title">現金繳費</div>
                    <b-alert show variant="info" class="mb-0">
                        此訂單採「現金結帳」，請於指定時間內至現場繳費，應付金額 {{ order.Payment.Amount | money(0, 2) }}。完成繳費後由後台確認收款，無須線上回報或上傳付款截圖。
                    </b-alert>
                    <div v-if="canCancelWholeOrder" class="d-flex flex-wrap justify-content-between align-items-center mt-3 pt-3 border-top">
                        <span class="small text-muted">不參加了？付款完成前可直接取消整筆訂單。</span>
                        <b-button size="sm" variant="outline-danger" :disabled="submitting"
                            @click="$emit('cancel-order', order)">取消整筆訂單</b-button>
                    </div>
                </template>

                <!-- 待付款：回報匯款結果 -->
                <template v-else-if="canReport">
                    <div class="camp-form-section-title">回報匯款結果</div>

                    <b-row>
                        <b-col cols="12" lg="5" class="mb-3 mb-lg-0">
                            <div v-if="bankInfo" class="camp-bank-box">
                                <div class="camp-bank-box-title">匯款資訊</div>
                                <dl class="camp-bank-list mb-0">
                                    <div>
                                        <dt>銀行</dt>
                                        <dd>{{ bankFullName || '-' }}</dd>
                                    </div>
                                    <div v-if="bankInfo.accountName">
                                        <dt>戶名</dt>
                                        <dd>{{ bankInfo.accountName }}</dd>
                                    </div>
                                    <div v-if="bankInfo.accountNo">
                                        <dt>帳號</dt>
                                        <dd>
                                            <span class="camp-account-tag">
                                                <span class="camp-account-no">{{ bankInfo.accountNo }}</span>
                                                <button type="button" class="camp-account-copy" @click="copyAccountNo"
                                                    v-b-tooltip.hover title="複製帳號">
                                                    <b-icon icon="clipboard"></b-icon>
                                                </button>
                                            </span>
                                        </dd>
                                    </div>
                                    <div>
                                        <dt>應匯金額</dt>
                                        <dd class="font-weight-bold">{{ order.Payment.Amount | money(0, 2) }}</dd>
                                    </div>
                                </dl>
                                <div v-if="bankInfo.paymentInfoHtml" class="small text-muted mt-2" v-html="bankInfo.paymentInfoHtml"></div>
                            </div>
                        </b-col>

                        <b-col cols="12" lg="7">
                            <p class="small text-muted mb-2">標示 <span class="text-danger">*</span> 為必填項目</p>
                            <b-form>
                                <b-row>
                                    <b-col cols="12" sm="6">
                                        <b-form-group>
                                            <template #label>付款日期 <span class="text-danger">*</span></template>
                                            <b-form-input type="date" v-model="reportForm.PaidDate" :min="minPaidDate"></b-form-input>
                                            <b-form-text v-if="minPaidDate" class="text-muted">
                                                不得早於訂單成立日（{{ minPaidDate }}）
                                            </b-form-text>
                                        </b-form-group>
                                    </b-col>
                                    <b-col cols="12" sm="6">
                                        <b-form-group>
                                            <template #label>帳號後五碼 <span class="text-danger">*</span></template>
                                            <b-form-input v-model="reportForm.AccountLast5" maxlength="5" inputmode="numeric"></b-form-input>
                                        </b-form-group>
                                    </b-col>
                                    <b-col cols="12" sm="6">
                                        <b-form-group>
                                            <template #label>付款人戶名 <span class="text-danger">*</span></template>
                                            <b-form-input v-model="reportForm.PayerName"></b-form-input>
                                        </b-form-group>
                                    </b-col>
                                    <b-col cols="12" sm="6">
                                        <b-form-group label="備註">
                                            <b-form-input v-model="reportForm.Memo"></b-form-input>
                                        </b-form-group>
                                    </b-col>
                                    <b-col cols="12">
                                        <b-form-group label="付款截圖（選填）" description="可協助加速對帳">
                                            <b-form-file v-model="proofFile" accept="image/jpeg,image/png,image/webp"
                                                :disabled="proofUploading" browse-text="選擇檔案"
                                                placeholder="未選擇檔案" @input="onProofSelected"></b-form-file>
                                            <div v-if="proofUploading" class="small text-muted mt-1">
                                                <b-spinner small></b-spinner> 上傳中...
                                            </div>
                                            <div v-else-if="proofUploaded" class="mt-2 d-flex align-items-center">
                                                <b-img v-if="proofPreviewUrl" :src="proofPreviewUrl" thumbnail width="80" class="mr-2"></b-img>
                                                <span class="small mr-2">{{ proofFileName }}</span>
                                                <b-button size="sm" variant="link" class="p-0" @click="clearProof">重新上傳</b-button>
                                            </div>
                                        </b-form-group>
                                    </b-col>
                                </b-row>
                                <div class="d-flex justify-content-end">
                                    <b-button size="sm" variant="primary" :disabled="reporting || proofUploading" @click="submitReport">
                                        {{ reporting ? '送出中...' : '送出匯款回報' }}
                                    </b-button>
                                </div>
                            </b-form>
                        </b-col>
                    </b-row>

                    <div v-if="canCancelWholeOrder" class="d-flex flex-wrap justify-content-between align-items-center mt-3 pt-3 border-top">
                        <span class="small text-muted">不參加了？付款回報前可直接取消整筆訂單。</span>
                        <b-button size="sm" variant="outline-danger" :disabled="submitting"
                            @click="$emit('cancel-order', order)">取消整筆訂單</b-button>
                    </div>
                </template>

                <!-- 已回報 / 已付款 / 對帳失敗 / 已取消：唯讀 -->
                <template v-else>
                    <div class="camp-form-section-title">匯款回報資料</div>
                    <dl class="camp-bank-list mb-0">
                        <div v-if="order.Payment.PaidDate">
                            <dt>付款日期</dt>
                            <dd>{{ order.Payment.PaidDate | datefmt('YYYY-MM-DD') }}</dd>
                        </div>
                        <div v-if="order.Payment.AccountLast5">
                            <dt>帳號後五碼</dt>
                            <dd>{{ order.Payment.AccountLast5 }}</dd>
                        </div>
                        <div v-if="order.Payment.PayerName">
                            <dt>付款人戶名</dt>
                            <dd>{{ order.Payment.PayerName }}</dd>
                        </div>
                    </dl>
                    <div v-if="paymentNote" class="mt-2">
                        <small class="text-muted">{{ paymentNote.label }}</small>
                        <div :class="{ 'text-danger': paymentNote.danger }">{{ paymentNote.value }}</div>
                    </div>
                    <div v-if="order.Payment.HasProofImage || canRetry" class="d-flex flex-wrap align-items-center mt-3">
                        <b-button v-if="order.Payment.HasProofImage" size="sm" variant="link" class="p-0 mr-3 mb-2"
                            @click="$emit('view-proof', order.Payment.Id)">查看付款截圖</b-button>
                        <b-button v-if="canRetry" size="sm" variant="outline-primary" class="mb-2"
                            :disabled="submitting" @click="$emit('retry', order)">重新付款</b-button>
                    </div>
                </template>

                <!-- 付款歷程：常駐最新一筆，其餘一般付款收進歷程（補收款維持獨立區塊） -->
                <div v-if="historyRows.length" class="camp-payment-history mt-3">
                    <button type="button" class="camp-history-toggle" :aria-expanded="historyOpen ? 'true' : 'false'"
                        @click="historyOpen = !historyOpen">
                        <span class="d-flex align-items-center">
                            <span class="camp-history-toggle-icon"><b-icon icon="clock"></b-icon></span>
                            <span class="font-weight-bold ml-2">付款歷程</span>
                            <b-badge variant="light" class="ml-2">{{ historyRows.length }}</b-badge>
                        </span>
                        <span class="small text-primary">
                            {{ historyOpen ? '收合' : '查看' }}
                            <b-icon :icon="historyOpen ? 'chevron-up' : 'chevron-down'" class="ml-1"></b-icon>
                        </span>
                    </button>
                    <b-collapse v-model="historyOpen" class="camp-payment-history-list mt-2">
                        <div v-for="h in historyRows" :key="h.PaymentId" class="camp-history-item"
                            :class="{ 'is-void': h.StatusNum === 50 }">
                            <button type="button" class="camp-history-summary camp-history-row-button d-flex justify-content-between align-items-center flex-wrap"
                                :aria-expanded="historyExpanded[h.PaymentId] ? 'true' : 'false'"
                                :aria-controls="'payment-history-' + h.PaymentId" @click="toggleHistoryRow(h.PaymentId)">
                                <span>
                                    <b-badge :variant="surchargeStateVariant(h.StatusNum)">{{ h.Status }}</b-badge>
                                    <span class="ml-2 font-weight-bold">{{ h.Amount | money(0, 2) }}</span>
                                </span>
                                <span class="small text-muted">
                                    <template v-if="h.PaymentMethod">{{ h.PaymentMethod }}｜</template>{{ h.Create | datefmt('YYYY-MM-DD HH:mm') }}
                                    <b-icon :icon="historyExpanded[h.PaymentId] ? 'chevron-up' : 'chevron-down'" class="ml-1"></b-icon>
                                </span>
                            </button>
                            <div v-if="h.StatusNum === 40 && h.RejectedReason" class="small text-danger mt-1">
                                對帳失敗原因：{{ h.RejectedReason }}
                            </div>
                            <b-collapse :id="'payment-history-' + h.PaymentId" :visible="!!historyExpanded[h.PaymentId]">
                                <dl class="camp-bank-list small mb-0 mt-2">
                                    <div v-if="h.PaidDate">
                                        <dt>付款日期</dt>
                                        <dd>{{ h.PaidDate | datefmt('YYYY-MM-DD') }}</dd>
                                    </div>
                                    <div v-if="h.AccountLast5">
                                        <dt>帳號後五碼</dt>
                                        <dd>{{ h.AccountLast5 }}</dd>
                                    </div>
                                    <div v-if="h.PayerName">
                                        <dt>付款人戶名</dt>
                                        <dd>{{ h.PayerName }}</dd>
                                    </div>
                                    <div v-if="h.Memo">
                                        <dt>{{ h.StatusNum === 50 ? '取消原因' : '匯款備註' }}</dt>
                                        <dd>{{ h.Memo }}</dd>
                                    </div>
                                </dl>
                                <b-button v-if="h.HasProofImage" size="sm" variant="link" class="p-0 mt-1"
                                    @click="$emit('view-proof', h.PaymentId)">查看付款截圖</b-button>
                            </b-collapse>
                        </div>
                    </b-collapse>
                </div>
            </template>

            <!-- ④-1 補收款項（轉班差額／檢定費等，獨立於訂單狀態） -->
            <template v-if="order.Surcharges && order.Surcharges.length">
                <hr class="camp-order-hr" />
                <div id="order-surcharges" class="camp-form-section-title camp-scroll-target">補收款項</div>
                <div v-for="s in order.Surcharges" :key="s.PaymentId" :id="'surcharge-' + s.PaymentId" class="camp-surcharge-item camp-scroll-target"
                    :class="{ 'is-void': s.StatusNum === 50 }">
                    <div class="d-flex justify-content-between align-items-center flex-wrap">
                        <span>
                            <b-badge :variant="surchargeStateVariant(s.StatusNum)">{{ s.Status }}</b-badge>
                            <span class="ml-2 font-weight-bold">{{ s.Amount | money(0, 2) }}</span>
                        </span>
                        <span v-if="surchargeMethodLabel(s)" class="small text-muted">{{ surchargeMethodLabel(s) }}</span>
                    </div>
                    <div v-if="s.Items && s.Items.length" class="small text-muted mt-1">
                        <div v-for="(it, i) in s.Items" :key="i">{{ it.FeeCategory }} {{ it.Amount | money(0, 2) }}</div>
                    </div>
                    <div v-if="s.Reason" class="small mt-1"><span class="text-muted">說明：</span>{{ s.Reason }}</div>

                    <div v-if="s.StatusNum === 30" class="small mt-2">
                        已收款<span v-if="s.ConfirmedAt || s.PaidDate">（{{ (s.ConfirmedAt || s.PaidDate) | datefmt('YYYY/MM/DD') }}）</span>
                        <b-button v-if="s.HasProofImage" size="sm" variant="link" class="p-0 ml-2"
                            @click="$emit('view-proof', s.PaymentId)">查看付款截圖</b-button>
                    </div>
                    <div v-else-if="s.StatusNum === 20" class="small text-info mt-2">款項核對中，請耐心等候。</div>
                    <div v-else-if="s.StatusNum === 40" class="small text-danger mt-2">
                        <span v-if="s.RejectedReason">對帳失敗：{{ s.RejectedReason }}。</span>如需重新繳費請聯絡客服。
                    </div>
                    <div v-else-if="s.CanReport" class="d-flex justify-content-between align-items-center flex-wrap mt-2">
                        <span class="small text-danger">尚未繳費</span>
                        <b-button size="sm" variant="primary" @click="$emit('pay-surcharge', s)">前往繳費</b-button>
                    </div>
                </div>
            </template>

            <!-- 對帳中：不可自行取消／申請退費，只提示聯絡客服 -->
            <template v-if="contactSupportOnly">
                <hr class="camp-order-hr" />
                <b-alert show variant="info" class="mb-0 small">{{ contactSupportReason }}</b-alert>
            </template>

            <!-- ⑤ 退費 -->
            <template v-else-if="canApplyRefundOrder">
                <hr class="camp-order-hr" />
                <div class="d-flex justify-content-between align-items-center">
                    <span class="small text-muted">如需退費，可對整筆訂單提出退費申請。</span>
                    <b-button size="sm" variant="outline-secondary" class="text-nowrap"
                        @click="$emit('apply-refund', { order })">申請整筆退費</b-button>
                </div>
            </template>
            <template v-else-if="applyRefundDisabledHint">
                <hr class="camp-order-hr" />
                <span class="small text-muted">{{ applyRefundDisabledHint }}</span>
            </template>

            <CampRefundSection :refunds="refunds" @changed="$emit('refund-changed')" />
        </b-card>
    </div>
</template>
<script>
import { apiCampPaymentReport, apiCampPaymentProofUpload, apiCampOrderPaymentHistory } from '@/api/API.js';
import CampRefundSection from '@/components/CampRefundSection.vue';
import { campOrderStateVariant, resolveCampOrderAttention } from '@/utils/campEnums.js';

export default {
    name: 'CampOrderPanel',
    components: { CampRefundSection },
    props: {
        order: { type: Object, required: true },
        submitting: { type: Boolean, default: false },
        bankInfo: { type: Object, default: null },
        refunds: { type: Array, default: () => [] },
        refundsLoading: { type: Boolean, default: false },
        refundsLoadError: { type: Boolean, default: false },
    },
    data() {
        return {
            reportForm: { PaidDate: '', AccountLast5: '', PayerName: '', Memo: '' },
            reporting: false,
            proofFile: null,
            proofUploading: false,
            proofUploaded: false,
            proofFileName: null,
            proofPreviewUrl: null,
            historyItems: [],
            historyOpen: false,
            historyExpanded: {},
            amountOpen: false,
            transferredDetailsOpen: false,
        };
    },
    watch: {
        'order.Id'(newId, oldId) {
            if (!oldId || newId === oldId) return;
            this.transferredDetailsOpen = false;
            this.amountOpen = false;
            this.historyOpen = false;
            this.historyExpanded = {};
        },
        historyKey: {
            immediate: true,
            handler() {
                this.loadHistory();
            },
        },
    },
    computed: {
        // 付款單或狀態改變（回報、重新付款、對帳結果）時重新載入歷程。
        historyKey() {
            const p = this.order.Payment;
            return p ? `${this.order.Id}|${p.Id}|${p.StatusNum}` : '';
        },
        // 歷程 = 一般付款扣掉常駐那筆；0 筆時不顯示入口。
        historyRows() {
            const p = this.order.Payment;
            if (!p) return [];
            return this.historyItems.filter((h) => h.PaymentId !== p.Id);
        },
        // 可操作條件一律讀後端旗標（08 票），前端不重寫狀態規則。
        actions() {
            return this.order.Actions || {};
        },
        attention() {
            if (this.refundsLoading) return null;
            return resolveCampOrderAttention(this.order, this.refunds, { refundsLoadError: this.refundsLoadError });
        },
        // 目前仍有效的報名優先；已轉出的舊明細保留查閱，但不搶主要視覺位置。
        orderedDetails() {
            return [...(this.order.Details || [])].sort((a, b) => Number(!!a.TransferredToTitle) - Number(!!b.TransferredToTitle));
        },
        transferredDetails() {
            return this.orderedDetails.filter((d) => !!d.TransferredToTitle);
        },
        mobileVisibleDetails() {
            if (this.transferredDetailsOpen) return this.orderedDetails;
            return this.orderedDetails.filter((d) => !d.TransferredToTitle);
        },
        orderStateVariant() {
            return campOrderStateVariant(this.order.OrderStateNum);
        },
        canReport() {
            return !!this.actions.CanReport;
        },
        isCounterPayment() {
            const p = this.order.Payment;
            return !!p && Number(p.PaymentMethodNum) === 1;
        },
        isPaid() {
            const p = this.order.Payment;
            return !!p && Number(p.StatusNum) === 30;
        },
        paidBannerText() {
            const p = this.order.Payment;
            if (this.isCounterPayment) return '已於現場完成繳費，並由承辦人員確認收款。';
            const d = p && p.PaidDate ? this.$options.filters.datefmt(p.PaidDate, 'YYYY/MM/DD') : '';
            return d ? `已於 ${d} 收到匯款並完成核帳。` : '已收到匯款並完成核帳。';
        },
        canCancelWholeOrder() {
            return !!this.actions.CanCancel;
        },
        canRetry() {
            return !!this.actions.CanRetryPayment;
        },
        contactSupportOnly() {
            return !!this.actions.ContactSupportOnly;
        },
        contactSupportReason() {
            return this.actions.ApplyRefundDisabledReason
                || this.actions.CancelDisabledReason
                || '訂單付款核帳中，如需協助請聯絡客服。';
        },
        canApplyRefundOrder() {
            return !!this.actions.CanApplyRefund;
        },
        // 已付款／退費中系列訂單但整筆退費被擋時，顯示後端給的原因；待付款／對帳失敗等狀態不顯示。
        applyRefundDisabledHint() {
            if (this.canApplyRefundOrder || this.contactSupportOnly) return '';
            if (![40, 50, 60, 70].includes(Number(this.order.OrderStateNum))) return '';
            return this.actions.ApplyRefundDisabledReason || '';
        },
        minPaidDate() {
            return this.order.Create
                ? this.$options.filters.datefmt(this.order.Create, 'YYYY-MM-DD')
                : '';
        },
        bankFullName() {
            if (!this.bankInfo) return '';
            return [this.bankInfo.bankName, this.bankInfo.bankBranch].filter(Boolean).join(' ');
        },
        paymentNote() {
            const p = this.order.Payment;
            if (!p) return null;
            if (p.Status === '對帳失敗' && p.RejectedReason) return { label: '對帳失敗原因', value: p.RejectedReason, danger: true };
            if (p.Status === '已取消' && p.Memo) return { label: '取消原因', value: p.Memo, danger: false };
            if (p.Memo) return { label: '匯款備註', value: p.Memo, danger: false };
            return null;
        },
    },
    methods: {
        copyText(text, successMessage) {
            if (!text) return;
            const done = () => this.$bvToast && this.$bvToast.toast
                ? this.$bvToast.toast(successMessage, { variant: 'success', noCloseButton: true, autoHideDelay: 1500 })
                : alert(successMessage);
            const fallback = () => {
                const textarea = document.createElement('textarea');
                textarea.value = text;
                textarea.setAttribute('readonly', '');
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.select();
                const copied = document.execCommand('copy');
                document.body.removeChild(textarea);
                if (copied) done();
                else alert(text);
            };
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text).then(done).catch(fallback);
            } else fallback();
        },
        copyOrderNo() {
            this.copyText(this.order && this.order.OrderNo, '已複製訂單編號');
        },
        handleAttentionAction() {
            if (!this.attention) return;
            if (this.attention.actionType === 'retry-payment') {
                this.$emit('retry', this.order);
                return;
            }
            if (this.attention.actionType === 'pay-surcharge') {
                this.$emit('pay-surcharge', this.attention.surcharge);
                return;
            }
            if (this.attention.actionType === 'reload-refunds') {
                this.$emit('reload-refunds');
                return;
            }
            if (this.attention.actionType !== 'scroll' || !this.attention.target) return;
            this.$nextTick(() => {
                const target = document.getElementById(this.attention.target);
                if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
        },
        formatMoney(v) {
            return this.$options.filters.money(v, 0, 2);
        },
        detailStatusVariant(status) {
            if (status === '已取消') return 'secondary';
            if (status === '申請退費中') return 'warning';
            return 'success';
        },
        loadHistory() {
            if (!this.order.Payment) {
                this.historyItems = [];
                return;
            }
            apiCampOrderPaymentHistory(this.order.Id)
                .then((res) => {
                    this.historyItems = res.data || [];
                })
                .catch(() => {
                    this.historyItems = [];
                });
        },
        toggleHistoryRow(paymentId) {
            this.$set(this.historyExpanded, paymentId, !this.historyExpanded[paymentId]);
        },
        surchargeStateVariant(statusNum) {
            return { 10: 'warning', 20: 'info', 30: 'success', 40: 'danger', 50: 'secondary' }[statusNum] || 'secondary';
        },
        surchargeMethodLabel(s) {
            if (s.PaymentMethod) return s.PaymentMethod;
            if (s.StatusNum === 10) return '線上匯款回報';
            return '';
        },
        copyAccountNo() {
            const no = this.bankInfo && this.bankInfo.accountNo;
            this.copyText(no, '已複製匯款帳號');
        },
        onProofSelected(file) {
            if (!file) return;
            if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
                alert('付款截圖僅接受 jpg、png、webp 格式');
                this.clearProof();
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                alert('付款截圖大小不可超過 5MB');
                this.clearProof();
                return;
            }
            const fd = new FormData();
            fd.append('file', file);
            this.proofUploading = true;
            apiCampPaymentProofUpload(this.order.Payment.Id, fd)
                .then(() => {
                    this.proofUploading = false;
                    this.proofUploaded = true;
                    this.proofFileName = file.name;
                    if (this.proofPreviewUrl) URL.revokeObjectURL(this.proofPreviewUrl);
                    this.proofPreviewUrl = URL.createObjectURL(file);
                })
                .catch(() => {
                    this.proofUploading = false;
                    this.clearProof();
                });
        },
        clearProof() {
            this.proofFile = null;
            this.proofUploaded = false;
            this.proofFileName = null;
            if (this.proofPreviewUrl) {
                URL.revokeObjectURL(this.proofPreviewUrl);
                this.proofPreviewUrl = null;
            }
        },
        submitReport() {
            if (!this.reportForm.PaidDate || !this.reportForm.AccountLast5 || !this.reportForm.PayerName) {
                alert('請填寫所有必填欄位');
                return;
            }
            if (this.minPaidDate && this.reportForm.PaidDate < this.minPaidDate) {
                alert('付款日期不得早於訂單成立日期（' + this.minPaidDate + '）');
                return;
            }
            const accountLast5 = String(this.reportForm.AccountLast5 || '').trim();
            if (!/^\d{5}$/.test(accountLast5)) {
                alert('帳號後五碼請填寫 5 位數字');
                return;
            }
            this.reporting = true;
            apiCampPaymentReport(this.order.Payment.Id, {
                PaidDate: this.reportForm.PaidDate,
                AccountLast5: accountLast5,
                PayerName: this.reportForm.PayerName,
                Memo: this.reportForm.Memo,
            })
                .then(() => {
                    this.reporting = false;
                    this.reportForm = { PaidDate: '', AccountLast5: '', PayerName: '', Memo: '' };
                    this.clearProof();
                    this.$emit('reported');
                })
                .catch(() => {
                    this.reporting = false;
                });
        },
    },
};
</script>
<style>
.camp-order-panel .camp-form-section-title {
    color: #6c757d;
    font-size: 0.875rem;
    font-weight: 700;
    margin-bottom: 0.75rem;
}

.camp-order-panel .camp-order-hr {
    margin: 1rem 0;
}

.camp-order-panel .camp-order-heading {
    min-width: 0;
    gap: 12px;
}

.camp-order-panel .camp-order-identity {
    flex: 1 1 auto;
    min-width: 0;
}

.camp-order-panel .camp-order-state {
    flex: 0 0 auto;
    margin-top: 3px;
}

.camp-order-panel .camp-order-number {
    overflow-wrap: anywhere;
}

.camp-order-panel .camp-order-copy {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: relative;
    width: 30px;
    height: 30px;
    border-radius: 6px;
    padding: 0;
    background: #e7f5ff;
    color: #007bff;
    font-size: 0.75rem;
    line-height: 1;
}

.camp-order-panel .camp-order-copy::before {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 44px;
    height: 44px;
    transform: translate(-50%, -50%);
    content: '';
}

.camp-order-panel .camp-order-copy:hover,
.camp-order-panel .camp-order-copy:focus {
    background: #d4edff;
    color: #0056b3;
    text-decoration: none;
}

.camp-order-panel .camp-attention-card {
    display: flex;
    align-items: center;
    gap: 12px;
    border: 1px solid #bee5eb;
    border-left-width: 4px;
    border-radius: 6px;
    padding: 14px 16px;
    background: #f2fbfd;
}

.camp-order-panel .camp-attention-card.is-needs-action {
    border-color: #f3c36b;
    background: #fff9ed;
}

.camp-order-panel .camp-attention-card.is-completed {
    border-color: #8fd19e;
    background: #f1faf3;
}

.camp-order-panel .camp-attention-card.is-informational {
    border-color: #ced4da;
    background: #f8f9fa;
}

.camp-order-panel .camp-attention-icon {
    flex: 0 0 auto;
    align-self: flex-start;
    color: #5b6b7a;
    font-size: 1.35rem;
    line-height: 1;
    margin-top: 3px;
}

.camp-order-panel .camp-attention-card.is-needs-action .camp-attention-icon {
    color: #d98200;
}

.camp-order-panel .camp-attention-card.is-completed .camp-attention-icon {
    color: #218838;
}

.camp-order-panel .camp-attention-content {
    flex: 1 1 auto;
    min-width: 0;
}

.camp-order-panel .camp-attention-eyebrow {
    color: #6c757d;
    font-size: 0.75rem;
    font-weight: 700;
}

.camp-order-panel .camp-attention-title {
    font-weight: 700;
    margin-top: 1px;
}

.camp-order-panel .camp-attention-description {
    color: #6c757d;
    font-size: 0.875rem;
    margin-top: 2px;
}

.camp-order-panel .camp-attention-action {
    flex: 0 0 auto;
}

.camp-order-panel .camp-finance-card {
    border: 1px solid #dee2e6;
    border-radius: 6px;
    padding: 12px;
    background: #fff;
}

.camp-order-panel .camp-finance-card.is-completed {
    border-left: 3px solid #28a745;
}

.camp-order-panel .camp-scroll-target {
    scroll-margin-top: 1rem;
}

.camp-order-panel .camp-surcharge-item {
    border: 1px solid #dee2e6;
    border-radius: 6px;
    padding: 10px 12px;
    margin-bottom: 8px;
    background: #fff;
}

.camp-order-panel .camp-surcharge-item:last-child {
    margin-bottom: 0;
}

.camp-order-panel .camp-surcharge-item.is-void {
    opacity: 0.55;
}

.camp-order-panel .camp-history-item {
    border: 1px solid #dbe7ef;
    border-left: 3px solid #78b7df;
    border-radius: 6px;
    padding: 10px 12px;
    margin-bottom: 8px;
    background: #fbfdff;
}

.camp-order-panel .camp-history-item:last-child {
    margin-bottom: 0;
}

.camp-order-panel .camp-history-item.is-void {
    opacity: 0.55;
    border-left-color: #adb5bd;
}

.camp-order-panel .camp-history-summary {
    cursor: pointer;
}

.camp-order-panel .camp-history-row-button {
    width: 100%;
    border: 0;
    padding: 0;
    background: transparent;
    color: inherit;
    text-align: left;
}

.camp-order-panel .camp-history-row-button:focus {
    outline: 2px solid #78b7df;
    outline-offset: 4px;
}

.camp-order-panel .camp-history-toggle {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    border: 1px solid #dbe7ef;
    border-radius: 6px;
    padding: 10px 12px;
    background: #f5faff;
    color: inherit;
    text-align: left;
}

.camp-order-panel .camp-history-toggle:hover,
.camp-order-panel .camp-history-toggle:focus {
    border-color: #78b7df;
    background: #eef8ff;
    outline: none;
}

.camp-order-panel .camp-history-toggle-icon {
    display: inline-flex;
    width: 28px;
    height: 28px;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: #dff1fc;
    color: #1d5f8a;
}

.camp-order-panel .camp-payment-history-list {
    border-left: 1px dashed #b8dff5;
    margin-left: 14px;
    padding-left: 14px;
}

.camp-order-panel .camp-detail-transferred {
    opacity: 0.6;
}

.camp-order-panel .camp-registration-table th,
.camp-order-panel .camp-registration-table td {
    vertical-align: middle;
    padding-top: 0.6rem;
    padding-bottom: 0.6rem;
}

.camp-order-panel .camp-reg-card {
    border: 1px solid #dee2e6;
    border-radius: 6px;
    padding: 10px 12px;
    margin-bottom: 8px;
    background: #fff;
}

.camp-order-panel .camp-reg-card-amount {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    border-top: 1px solid #f0f0f0;
    padding-top: 8px;
}

.camp-order-panel .camp-mobile-more-details {
    display: block;
    width: 100%;
    border: 1px dashed #b8dff5;
    border-radius: 6px;
    padding: 8px 12px;
    background: #f8fcff;
    text-align: center;
}

.camp-order-panel .camp-mobile-collapse-toggle {
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    padding: 10px 12px;
    background: #fff;
    color: inherit;
    text-align: left;
}

.camp-order-panel .camp-amount-summary {
    max-width: 320px;
    margin-left: auto;
}

.camp-order-panel .camp-amount-summary > div,
.camp-order-panel .camp-bank-list > div {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    padding: 3px 0;
}

.camp-order-panel .camp-amount-summary dt,
.camp-order-panel .camp-bank-list dt {
    font-weight: 400;
    color: #6c757d;
    margin: 0;
    flex: 0 0 auto;
    padding-right: 12px;
}

.camp-order-panel .camp-amount-summary dd,
.camp-order-panel .camp-bank-list dd {
    margin: 0;
    text-align: right;
    word-break: break-all;
}

.camp-order-panel .camp-amount-total {
    border-top: 1px solid #dee2e6;
    margin-top: 4px;
    padding-top: 6px !important;
}

.camp-order-panel .camp-amount-total dt,
.camp-order-panel .camp-amount-total dd {
    font-weight: 700;
    color: inherit;
    font-size: 1.05rem;
}

.camp-order-panel .camp-bank-box {
    border: 1px solid #78b7df;
    border-radius: 6px;
    background: #eef8ff;
    padding: 12px;
}

.camp-order-panel .camp-bank-box-title {
    font-weight: 700;
    color: #1d5f8a;
    margin-bottom: 6px;
}

.camp-order-panel .camp-account-tag {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 2px 2px 2px 10px;
    border: 1px solid #78b7df;
    border-radius: 4px;
    background: #fff;
    color: #1d5f8a;
    max-width: 100%;
}

.camp-order-panel .camp-account-no {
    font-weight: 700;
    letter-spacing: 0.04em;
    word-break: break-all;
}

.camp-order-panel .camp-account-copy {
    flex: 0 0 auto;
    width: 26px;
    height: 26px;
    padding: 0;
    border: 0;
    border-radius: 4px;
    background: transparent;
    color: #1d5f8a;
}

.camp-order-panel .camp-account-copy:hover {
    background: #d8edf9;
}

@media (max-width: 767.98px) {
    .camp-order-panel > .card > .card-body {
        padding: 1rem;
    }

    .camp-order-panel .camp-attention-card {
        align-items: flex-start;
        flex-wrap: wrap;
        padding: 14px;
    }

    .camp-order-panel .camp-attention-content {
        flex-basis: calc(100% - 42px);
    }

    .camp-order-panel .camp-attention-action {
        width: 100%;
        margin-top: 2px;
    }

    .camp-order-panel .camp-amount-summary {
        max-width: none;
        margin-left: 0;
        padding: 10px 12px;
        border: 1px solid #dee2e6;
        border-radius: 6px;
        background: #f8f9fa;
    }

    .camp-order-panel .camp-bank-list {
        padding-right: 8px;
        padding-left: 8px;
    }

    .camp-order-panel .camp-history-summary > span {
        width: 100%;
    }

    .camp-order-panel .camp-history-summary > span + span {
        margin-top: 4px;
    }

    .camp-order-panel .camp-payment-history-list {
        margin-left: 12px;
        padding-left: 10px;
    }
}
</style>
